import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from "react";
import en, { type Translations } from "./en";
import ar from "./ar";

type Lang = "en" | "ar";

interface I18nContextType {
  lang: Lang;
  t: Translations;
  setLang: (lang: Lang) => void;
  dir: "ltr" | "rtl";
}

const translations: Record<Lang, Translations> = { en, ar };

const I18nContext = createContext<I18nContextType>({
  lang: "en",
  t: en,
  setLang: () => {},
  dir: "ltr",
});

export function I18nProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(() => {
    const saved = localStorage.getItem("openstore-lang");
    return (saved === "ar" || saved === "en") ? saved : "ar";
  });

  const t = translations[lang];
  const dir = t.dir;

  const setLang = useCallback((newLang: Lang) => {
    setLangState(newLang);
    localStorage.setItem("openstore-lang", newLang);
  }, []);

  useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.dir = dir;

    const themeFont = document.documentElement.style.getPropertyValue("--theme-font-ar")?.trim();
    const themeFontEn = document.documentElement.style.getPropertyValue("--theme-font-en")?.trim();

    if ((dir as string) === "rtl") {
      const arFont = themeFont || "Tajawal";
      const enFallback = themeFontEn || "Inter";
      document.documentElement.style.setProperty("--app-font-sans", `"${arFont}", "${enFallback}", sans-serif`);
    } else {
      const enFont = themeFontEn || "Inter";
      document.documentElement.style.setProperty("--app-font-sans", `"${enFont}", sans-serif`);
    }
    document.body.style.fontFamily = "";
  }, [lang, dir]);

  return (
    <I18nContext.Provider value={{ lang, t, setLang, dir }}>
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n() {
  return useContext(I18nContext);
}

export function useLocalized() {
  const { lang } = useI18n();
  return function localized<T extends Record<string, any>>(
    item: T,
    field: string,
    arField?: string
  ): string {
    const arKey = arField || (field + "Ar");
    if (lang === "ar") {
      return item[arKey] || item[field] || "";
    }
    return item[field] || item[arKey] || "";
  };
}

export type { Lang, Translations };
